import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createDefaultGoogleDraft,
  createGoogleSegmentId,
  GOOGLE_DEFAULT_PROMPT,
  GoogleAiVoiceDraftV1,
  GoogleAiVoiceSegment,
  GoogleSpeakerConfig,
  GoogleSpeakerSlot,
  GoogleTtsModel,
  GoogleVoiceOption,
  GOOGLE_TTS_MODEL,
  isGoogleTtsModel,
} from '../types';
import { canonicalizeGoogleDraft } from '../utils/canonicalizeGoogleDraft';
import { hasVoicesInCatalog, reconcileDraftWithCatalog, resolveVoicesForLanguage } from '../utils/voiceCatalog';

interface GoogleDraftStore {
  getGoogleAIVoiceDraft: () => Promise<GoogleAiVoiceDraftV1>;
  getGoogleAIVoiceDraftWithMeta?: () => Promise<{ draft: GoogleAiVoiceDraftV1; exists: boolean }>;
  saveGoogleAIVoiceDraft: (draft: GoogleAiVoiceDraftV1) => Promise<boolean>;
}

interface UseGoogleVoiceDraftArgs {
  propertyStore: GoogleDraftStore;
  voicesByLanguage: Record<string, GoogleVoiceOption[]>;
  canReconcileWithCatalog: boolean;
}

interface UseGoogleVoiceDraftReturn {
  draft: GoogleAiVoiceDraftV1;
  loadingDraft: boolean;
  draftError: string | null;
  normalizedDraft: GoogleAiVoiceDraftV1;
  hasTextToGenerate: boolean;
  singleSpeakerText: string;
  setDraftError: (value: string | null) => void;
  setPrompt: (prompt: string) => void;
  setPromptSpeakerA: (prompt: string) => void;
  setPromptSpeakerB: (prompt: string) => void;
  setModel: (model: GoogleTtsModel) => void;
  setLanguageCode: (languageCode: string) => void;
  setLanguageCodeWithoutRemember: (languageCode: string) => void;
  setMultiSpeaker: (checked: boolean) => void;
  setSpeakerA: (speaker: GoogleSpeakerConfig) => void;
  setSpeakerB: (speaker: GoogleSpeakerConfig) => void;
  setSingleSpeakerText: (text: string) => void;
  updateSegment: (index: number, next: GoogleAiVoiceSegment) => void;
  deleteSegment: (index: number) => void;
  addSegment: () => void;
}

const hasDraftTextContent = (draft: GoogleAiVoiceDraftV1) => draft.segments.some((segment) => segment.text.trim().length > 0);

const sanitizeDraft = (storedDraft: GoogleAiVoiceDraftV1): GoogleAiVoiceDraftV1 => ({
  ...storedDraft,
  model: isGoogleTtsModel(storedDraft.model) ? storedDraft.model : GOOGLE_TTS_MODEL,
  languageCode: String(storedDraft.languageCode ?? '').trim(),
  multiSpeaker: Boolean(storedDraft.multiSpeaker),
  promptSpeakerA: String(storedDraft.promptSpeakerA ?? ''),
  promptSpeakerB: String(storedDraft.promptSpeakerB ?? ''),
  speakers: {
    speakerA: {
      voiceId: String(storedDraft.speakers?.speakerA?.voiceId ?? '').trim(),
      displayName: String(storedDraft.speakers?.speakerA?.displayName ?? '').trim(),
    },
    speakerB: {
      voiceId: String(storedDraft.speakers?.speakerB?.voiceId ?? '').trim(),
      displayName: String(storedDraft.speakers?.speakerB?.displayName ?? '').trim(),
    },
  },
  segments:
    Array.isArray(storedDraft.segments) && storedDraft.segments.length > 0
      ? storedDraft.segments.map((segment) => ({
          id: String(segment.id ?? '').trim() || createGoogleSegmentId(),
          speaker: segment.speaker === 'B' ? 'B' : 'A',
          text: String(segment.text ?? ''),
        }))
      : [{ id: createGoogleSegmentId(), speaker: 'A', text: '' }],
  prompt: storedDraft.prompt && storedDraft.prompt.trim().length > 0 ? storedDraft.prompt : GOOGLE_DEFAULT_PROMPT,
});

const useGoogleVoiceDraft = ({ propertyStore, voicesByLanguage, canReconcileWithCatalog }: UseGoogleVoiceDraftArgs): UseGoogleVoiceDraftReturn => {
  const [draft, setDraft] = useState<GoogleAiVoiceDraftV1>(() => createDefaultGoogleDraft());
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [hasDraftFile, setHasDraftFile] = useState<boolean | null>(null);
  const lastSavedCanonicalRef = useRef<string | null>(null);
  const hasPersistedContentRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);
  const latestDraftRef = useRef(draft);
  const isMountedRef = useRef(true);

  useEffect(() => {
    latestDraftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const persistDraft = useCallback(
    async (draftToPersist: GoogleAiVoiceDraftV1) => {
      const hasTextContent = hasDraftTextContent(draftToPersist);
      if (!hasTextContent && !hasPersistedContentRef.current) return false;

      const canonicalDraft = canonicalizeGoogleDraft(draftToPersist);
      if (canonicalDraft === lastSavedCanonicalRef.current) return false;

      try {
        const saved = await propertyStore.saveGoogleAIVoiceDraft(draftToPersist);
        if (!saved) return false;

        lastSavedCanonicalRef.current = canonicalDraft;
        hasPersistedContentRef.current = hasTextContent;
        return true;
      } catch {
        if (isMountedRef.current) {
          setDraftError('Unable to save AI Voice draft for this page.');
        }
        return false;
      }
    },
    [propertyStore],
  );

  useEffect(() => {
    let isMounted = true;

    const loadDraft = async () => {
      try {
        const loaded =
          typeof propertyStore.getGoogleAIVoiceDraftWithMeta === 'function'
            ? await propertyStore.getGoogleAIVoiceDraftWithMeta()
            : { draft: await propertyStore.getGoogleAIVoiceDraft(), exists: true };
        if (!isMounted) return;
        const sanitizedDraft = sanitizeDraft(loaded.draft);
        lastSavedCanonicalRef.current = canonicalizeGoogleDraft(sanitizedDraft);
        hasPersistedContentRef.current = loaded.exists;
        setDraft(sanitizedDraft);
        setHasDraftFile(loaded.exists);
      } catch {
        if (!isMounted) return;
        const fallbackDraft = createDefaultGoogleDraft();
        lastSavedCanonicalRef.current = canonicalizeGoogleDraft(fallbackDraft);
        hasPersistedContentRef.current = false;
        setDraft(fallbackDraft);
        setHasDraftFile(false);
      } finally {
        if (isMounted) {
          setLoadingDraft(false);
        }
      }
    };

    void loadDraft();

    return () => {
      isMounted = false;
    };
  }, [propertyStore]);

  useEffect(() => {
    if (loadingDraft) return;
    if (hasDraftFile === null) return;
    if (!canReconcileWithCatalog) return;
    if (!hasVoicesInCatalog(voicesByLanguage)) return;

    setDraft((current) => reconcileDraftWithCatalog(current, voicesByLanguage));
  }, [canReconcileWithCatalog, hasDraftFile, loadingDraft, voicesByLanguage]);

  useEffect(() => {
    if (loadingDraft) return;
    const canonicalDraft = canonicalizeGoogleDraft(draft);
    const hasTextContent = hasDraftTextContent(draft);
    if (!hasTextContent && !hasPersistedContentRef.current) return;
    if (canonicalDraft === lastSavedCanonicalRef.current) return;

    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      void persistDraft(draft);
    }, 700);

    return () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [draft, loadingDraft, persistDraft]);

  useEffect(
    () => () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }

      void persistDraft(latestDraftRef.current);
    },
    [persistDraft],
  );

  const normalizedDraft = useMemo<GoogleAiVoiceDraftV1>(() => {
    if (draft.multiSpeaker) {
      return {
        ...draft,
        segments: draft.segments.length > 0 ? draft.segments : [{ id: createGoogleSegmentId(), speaker: 'A', text: '' }],
      };
    }

    const firstSegment = draft.segments[0] ?? { id: createGoogleSegmentId(), speaker: 'A' as GoogleSpeakerSlot, text: '' };
    return {
      ...draft,
      multiSpeaker: false,
      segments: [{ id: firstSegment.id, speaker: 'A', text: firstSegment.text }],
    };
  }, [draft]);

  const hasTextToGenerate = useMemo(() => normalizedDraft.segments.some((segment) => segment.text.trim().length > 0), [normalizedDraft.segments]);

  const singleSpeakerText = draft.segments[0]?.text ?? '';

  const setPrompt = useCallback((prompt: string) => {
    setDraft((current) => ({ ...current, prompt }));
  }, []);

  const setPromptSpeakerA = useCallback((promptSpeakerA: string) => {
    setDraft((current) => ({ ...current, promptSpeakerA }));
  }, []);

  const setPromptSpeakerB = useCallback((promptSpeakerB: string) => {
    setDraft((current) => ({ ...current, promptSpeakerB }));
  }, []);

  const setModel = useCallback((model: GoogleTtsModel) => {
    setDraft((current) => ({ ...current, model }));
  }, []);

  const applyLanguageCode = useCallback(
    (languageCode: string, remember: boolean) => {
      const normalizedLanguageCode = languageCode.trim();

      if (remember) {
        try {
          window.localStorage.setItem('languageCode', normalizedLanguageCode);
        } catch {
          // ignore localStorage failures
        }
      }

      setDraft((current) => {
        const languageVoices = resolveVoicesForLanguage(voicesByLanguage, normalizedLanguageCode);
        if (languageVoices.length < 1) {
          return { ...current, languageCode: normalizedLanguageCode };
        }

        const speakerA = languageVoices.find((voice) => voice.voiceId === current.speakers.speakerA.voiceId) ?? languageVoices[0];
        const speakerB = languageVoices.find((voice) => voice.voiceId === current.speakers.speakerB.voiceId) ?? languageVoices[Math.min(1, languageVoices.length - 1)] ?? speakerA;

        return {
          ...current,
          languageCode: normalizedLanguageCode,
          speakers: {
            speakerA: { voiceId: speakerA.voiceId, displayName: speakerA.displayName },
            speakerB: { voiceId: speakerB.voiceId, displayName: speakerB.displayName },
          },
        };
      });
    },
    [voicesByLanguage],
  );

  const setLanguageCode = useCallback((languageCode: string) => {
    applyLanguageCode(languageCode, true);
  }, [applyLanguageCode]);

  const setLanguageCodeWithoutRemember = useCallback((languageCode: string) => {
    applyLanguageCode(languageCode, false);
  }, [applyLanguageCode]);

  const setMultiSpeaker = useCallback((checked: boolean) => {
    setDraft((current) => ({ ...current, multiSpeaker: checked }));
  }, []);

  const setSpeaker = useCallback((slot: GoogleSpeakerSlot, speaker: GoogleSpeakerConfig) => {
    setDraft((current) => ({
      ...current,
      speakers: {
        ...current.speakers,
        [slot === 'A' ? 'speakerA' : 'speakerB']: speaker,
      },
    }));
  }, []);

  const setSpeakerA = useCallback(
    (speaker: GoogleSpeakerConfig) => {
      try {
        window.localStorage.setItem('voiceSpeakerA', JSON.stringify(speaker));
      } catch {
        // ignore localStorage failures
      }
      setSpeaker('A', speaker);
    },
    [setSpeaker],
  );
  const setSpeakerB = useCallback(
    (speaker: GoogleSpeakerConfig) => {
      try {
        window.localStorage.setItem('voiceSpeakerB', JSON.stringify(speaker));
      } catch {
        // ignore localStorage failures
      }
      setSpeaker('B', speaker);
    },
    [setSpeaker],
  );

  const updateSegment = useCallback((index: number, next: GoogleAiVoiceSegment) => {
    setDraft((current) => {
      const nextSegments = [...current.segments];
      nextSegments[index] = next;
      return { ...current, segments: nextSegments };
    });
  }, []);

  const deleteSegment = useCallback((index: number) => {
    setDraft((current) => {
      if (current.segments.length <= 1) return current;
      return { ...current, segments: current.segments.filter((_segment, idx) => idx !== index) };
    });
  }, []);

  const addSegment = useCallback(() => {
    setDraft((current) => ({
      ...current,
      segments: [...current.segments, { id: createGoogleSegmentId(), speaker: 'A', text: '' }],
    }));
  }, []);

  const setSingleSpeakerText = useCallback((text: string) => {
    setDraft((current) => {
      const firstSegment = current.segments[0] ?? { id: createGoogleSegmentId(), speaker: 'A' as GoogleSpeakerSlot, text: '' };
      return {
        ...current,
        segments: [{ ...firstSegment, speaker: 'A', text }],
      };
    });
  }, []);

  return {
    draft,
    loadingDraft,
    draftError,
    normalizedDraft,
    hasTextToGenerate,
    singleSpeakerText,
    setDraftError,
    setPrompt,
    setPromptSpeakerA,
    setPromptSpeakerB,
    setModel,
    setLanguageCode,
    setLanguageCodeWithoutRemember,
    setMultiSpeaker,
    setSpeakerA,
    setSpeakerB,
    setSingleSpeakerText,
    updateSegment,
    deleteSegment,
    addSegment,
  };
};

export default useGoogleVoiceDraft;
